import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';
import { X, User, Mail, Lock, Shield, Calendar } from 'lucide-react';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'master'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.getUsers();
      
      // Проверяем что пришёл массив
      const usersData = response.data?.data || response.data || [];
      setUsers(Array.isArray(usersData) ? usersData : []);
      
      console.log('✅ Загружены пользователи:', usersData);
    } catch (error) {
      console.error('❌ Ошибка загрузки пользователей:', error);
      setUsers([]); // Устанавливаем пустой массив при ошибке
    } finally {
      setLoading(false);
    }
  };

  const handleView = (user) => {
    setViewingUser(user);
    setIsEditing(false);
    setFormData({
      username: user.username,
      email: user.email || '',
      password: '',
      role: user.role
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = { ...formData };
      
      // Если редактируем и пароль пустой - не отправляем его
      if (viewingUser && isEditing && !formData.password) {
        delete submitData.password;
      }

      if (viewingUser && isEditing) {
        // Обновление
        if (typeof api.updateUser === 'function') {
          await api.updateUser(viewingUser._id, submitData);
        } else {
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/users/${viewingUser._id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(submitData)
          });

          if (!response.ok) {
            throw new Error(`Ошибка обновления: ${response.status}`);
          }
        }
      } else {
        // Создание
        if (typeof api.createUser === 'function') {
          await api.createUser(submitData);
        } else {
          const token = localStorage.getItem('token');
          const response = await fetch('/api/users', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(submitData)
          });

          if (!response.ok) {
            throw new Error(`Ошибка создания: ${response.status}`);
          }
        }
      }
      
      setShowModal(false);
      setViewingUser(null);
      setIsEditing(false);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'master'
      });
      await loadUsers();
    } catch (error) {
      console.error('Ошибка сохранения пользователя:', error);
      alert('Ошибка при сохранении пользователя: ' + (error.message || ''));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!viewingUser) return;
    
    if (!window.confirm(`Вы уверены, что хотите удалить пользователя "${viewingUser.username}"?`)) {
      return;
    }

    setSubmitting(true);
    try {
      if (typeof api.deleteUser === 'function') {
        await api.deleteUser(viewingUser._id);
      } else {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/users/${viewingUser._id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Ошибка удаления: ${response.status}`);
        }
      }

      setShowModal(false);
      setViewingUser(null);
      setIsEditing(false);
      await loadUsers();
    } catch (error) {
      console.error('Ошибка удаления пользователя:', error);
      alert('Ошибка при удалении пользователя: ' + (error.message || ''));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = useMemo(() =>
    users.filter(u =>
      u.username.toLowerCase().includes(search.toLowerCase()) ||
      (u.email && u.email.toLowerCase().includes(search.toLowerCase())) ||
      u.role.toLowerCase().includes(search.toLowerCase())
    ),
    [users, search]
  );

  const getRoleColor = (role) => {
    const colors = {
      'admin': 'bg-purple-600/20 text-purple-400 border-purple-500/30',
      'master': 'bg-blue-600/20 text-blue-400 border-blue-500/30',
      'manager': 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30'
    };
    return colors[role] || colors['master'];
  };

  const getRoleText = (role) => {
    const roles = {
      'admin': 'Администратор',
      'master': 'Мастер',
      'manager': 'Менеджер'
    };
    return roles[role] || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загрузка данных...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Пользователи</h1>
          <p className="text-gray-400 mt-1">Всего пользователей: {users.length}</p>
        </div>
        <button
          onClick={() => {
            setViewingUser(null);
            setIsEditing(false);
            setFormData({ username: '', email: '', password: '', role: 'master' });
            setShowModal(true);
          }}
          className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-all shadow-lg hover:shadow-blue-500/50"
        >
          Добавить пользователя
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-3">
          <input
            type="text"
            placeholder="Поиск по имени, email или роли..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent text-white placeholder-gray-400 focus:outline-none text-sm"
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-700/50">
          <thead className="bg-gray-700/30">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Пользователь
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Роль
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Дата создания
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {filteredUsers.map((user) => (
              <tr 
                key={user._id} 
                className="hover:bg-gray-700/20 transition-colors cursor-pointer"
                onClick={() => handleView(user)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-600/20 border border-blue-500/30 rounded-full flex items-center justify-center">
                      <span className="text-blue-400 font-semibold">
                        {user.username.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-white">{user.username}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                  {user.email || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRoleColor(user.role)}`}>
                    {getRoleText(user.role)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString('ru-RU') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {filteredUsers.map((user) => (
          <div 
            key={user._id} 
            className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4 hover:border-gray-600/50 transition-all cursor-pointer"
            onClick={() => handleView(user)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="h-12 w-12 bg-blue-600/20 border border-blue-500/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-400 font-bold text-lg">
                    {user.username.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-base">{user.username}</h3>
                  <p className="text-gray-500 text-xs">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('ru-RU', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric' 
                    }) : '—'}
                  </p>
                </div>
              </div>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getRoleColor(user.role)}`}>
                {getRoleText(user.role)}
              </span>
            </div>
            
            {user.email && (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Mail className="w-4 h-4" />
                <span>{user.email}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700/50">
          <h3 className="text-xl font-semibold text-white mb-2">
            {search ? 'Пользователи не найдены' : 'Нет пользователей'}
          </h3>
          <p className="text-gray-400 mb-6">
            {search ? 'Попробуйте изменить параметры поиска' : 'Добавьте первого пользователя'}
          </p>
          {!search && (
            <button 
              onClick={() => {
                setViewingUser(null);
                setIsEditing(false);
                setFormData({ username: '', email: '', password: '', role: 'master' });
                setShowModal(true);
              }} 
              className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-all"
            >
              Добавить пользователя
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {viewingUser && !isEditing ? 'Информация о пользователе' : viewingUser ? 'Редактировать пользователя' : 'Новый пользователь'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setViewingUser(null);
                    setIsEditing(false);
                  }}
                  className="text-gray-400 hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {viewingUser && !isEditing ? (
                <div className="space-y-4">
                  <div className="bg-gray-700/30 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-16 w-16 bg-blue-600/20 border border-blue-500/30 rounded-full flex items-center justify-center">
                        <span className="text-blue-400 font-bold text-2xl">
                          {viewingUser.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg">{viewingUser.username}</h3>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRoleColor(viewingUser.role)}`}>
                          {getRoleText(viewingUser.role)}
                        </span>
                      </div>
                    </div>
                    
                    {viewingUser.email && (
                      <div className="flex items-center gap-2 pt-3 border-t border-gray-600/50">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-white">{viewingUser.email}</span>
                      </div>
                    )}
                    
                    {viewingUser.createdAt && (
                      <div className="flex items-center gap-2 pt-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-400 text-sm">
                          Создан: {new Date(viewingUser.createdAt).toLocaleDateString('ru-RU')}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleDelete}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      {submitting ? 'Удаление...' : 'Удалить'}
                    </button>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      Изменить
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Username */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Имя пользователя *
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      required
                      placeholder="Введите имя пользователя"
                      className="input-field"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="email@example.com"
                      className="input-field"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Пароль {viewingUser && isEditing ? '(оставьте пустым, чтобы не менять)' : '*'}
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required={!viewingUser}
                      placeholder="Введите пароль"
                      className="input-field"
                    />
                  </div>

                  {/* Role */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Роль
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      className="input-field"
                    >
                      <option value="master">Мастер</option>
                      <option value="admin">Администратор</option>
                      <option value="manager">Менеджер</option>
                    </select>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (viewingUser) {
                          setIsEditing(false);
                        } else {
                          setShowModal(false);
                        }
                      }}
                      className="flex-1 btn-secondary"
                      disabled={submitting}
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      {submitting ? 'Сохранение...' : (viewingUser ? 'Сохранить' : 'Создать')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;